import { api } from '../api.js';
import { store } from '../store.js';
import { navigate } from '../router.js';
import { renderHeader, bindHeader } from '../components/header.js';
import { KeystrokeTracker } from '../components/KeystrokeTracker.js';
import { KeystrokeDNA } from '../components/KeystrokeDNA.js';
import { shouldShowMobileWarning } from '../utils/deviceDetect.js';
import { showMobileWarning } from '../components/mobileWarning.js';
import { toast } from '../utils/toast.js';

const STAGE_LABELS = {
  initial_typing: 'Стадия 1 · Наработка',
  drum_hint: 'Стадия 2 · Барабан (с подсказкой)',
  drum_blind: 'Стадия 3 · Барабан (без подсказки)',
  complete: 'Выучено',
};

const PHASE_LEARN_STAGES = new Set(['initial_typing', 'drum_hint']);

function phaseFor(stage) {
  return PHASE_LEARN_STAGES.has(stage) ? 'learn' : 'verify';
}

const STAGE_ROADMAP = [
  { key: 'initial_typing', label: 'Знакомство' },
  { key: 'drum_hint',      label: 'Подсказка'  },
  { key: 'drum_blind',     label: 'Вслепую'    },
  { key: 'complete',       label: 'Мастер'     },
];

function subStageLabel(progress) {
  switch (progress.stage) {
    case 'initial_typing':
      return `Знакомство · ${Math.min(progress.initialRepsDone + 1, 15)} из 15`;
    case 'drum_hint':
      return `Подсказка · ${progress.drumHintSuccessCount} из 3 подряд`;
    case 'drum_blind':
      return `Вслепую · ${progress.drumBlindSuccessCount} из 2 подряд`;
    default:
      return '';
  }
}

const STAGE_TRANSITIONS = {
  drum_hint: {
    icon: '📥',
    bg: 'linear-gradient(135deg, #b3ff3a, #3af2ff)',
    kicker: 'Шаг 1 пройден',
    title: '«{phrase}» уходит в Закрепление',
    body: 'Пальцы её запомнили — дальше она будет возвращаться вперемешку с другими фразами (это и есть Закрепление). Пока возьмёмся за новую, свежую фразу в Знакомстве.',
    rules: [
      'Фраза никуда не пропадает — отдыхает до Закрепления',
      'Закрепление случайно подкинет её обратно с подсказкой',
      'Нужно набрать 3 раза подряд правильно, чтобы она пошла на финальную Проверку',
    ],
    cta: 'Погнали к новой',
  },
  drum_blind: {
    icon: '🎯',
    bg: 'linear-gradient(135deg, #3af2ff, #ff3aa4)',
    kicker: 'Шаг 2 пройден',
    title: 'Финальная проверка',
    body: 'Английский теперь спрятан — нужно вспомнить фразу самому. Доверься памяти, пальцы уже знают.',
    rules: [
      'Подсказки нет — только русский перевод',
      'Правильно 2 раза подряд — фраза выучена навсегда',
      'Не получилось — откат на Закрепление, попробуем ещё',
    ],
    cta: 'Поехали, я готов',
  },
  ejected: {
    icon: '🌀',
    bg: 'linear-gradient(135deg, #ffcf47, #ff5170)',
    kicker: 'Без паники',
    title: 'Эта фраза ускользает',
    body: 'Бывает. Отправляем её на переучивание с самого начала — через пару кругов она сдастся. Никакого штрафа, счётчики общего прогресса не страдают.',
    rules: [
      'Фраза возвращается на Шаг 1 · Знакомство',
      'Пока она отдыхает, возьмёмся за другие',
      'Встретится снова — будет даваться легче',
    ],
    cta: 'Понял, погнали дальше',
  },
  parked: {
    icon: '🅿️',
    bg: 'linear-gradient(135deg, #3af2ff, #b3ff3a)',
    kicker: 'Откладываем',
    title: '«{phrase}» — на полку',
    body: 'Не зацикливаемся. Эта фраза возвращается, когда станешь объективно сильнее — после 15 новых выученных. Пока — другие, более «твои».',
    rules: [
      'Фраза не теряется — ждёт своего часа',
      'Вернётся автоматически, когда наберёшь обороты',
      'Можешь форсировать возврат вручную в коллекции',
    ],
    cta: 'Логично, погнали',
  },
};

const STAGE_DESCRIPTIONS = {
  initial_typing:
    'Знакомимся с фразой. Просто набирайте её — пальцы запомнят раньше, чем голова. Английский рядом, если что. Медленно или с ошибками — это норм, так и должно быть. Дальше пойдёшь, когда почувствуешь уверенность.',
  drum_hint:
    'Фраза теперь появляется вперемешку с другими — чтобы не зубрить подряд. Английский пока можно подсматривать. Наберёшь правильно 3 раза — и она перейдёт на финальную проверку.',
  drum_blind:
    'Финальный шаг. Английский спрятан, но ты уже знаешь — просто доверься памяти. 2 правильных раза — и фраза с тобой навсегда. Не получилось — ничего страшного, вернёмся на шаг назад и ещё попробуем.',
  complete: '',
};

function renderPhaseHeader(stage, progress, showDesc) {
  const desc = STAGE_DESCRIPTIONS[stage] || '';
  const currentIdx = STAGE_ROADMAP.findIndex((s) => s.key === stage);
  const items = STAGE_ROADMAP.map((s, i) => {
    let cls = '';
    if (i < currentIdx) cls = 'done';
    else if (i === currentIdx) cls = 'active';
    const mark = i < currentIdx ? ' ✓' : '';
    return `<span class="phase ${cls}">${s.label}${mark}</span>`;
  }).join('<span class="sep">→</span>');
  return `
    <div class="phase-header">
      <div class="phase-breadcrumb roadmap">${items}</div>
      <div class="sub-stage text-center text-muted">
        <span>${subStageLabel(progress)}</span>
        ${desc ? `<button type="button" class="info-btn" id="toggle-desc" aria-label="Что делаем на этом шаге" title="Что делаем на этом шаге">ℹ</button>` : ''}
      </div>
      ${desc && showDesc ? `<div class="stage-desc text-muted text-center">${desc}</div>` : ''}
    </div>
  `;
}

export async function renderTrainerPage({ root }) {
  if (shouldShowMobileWarning()) {
    await showMobileWarning();
  }

  root.innerHTML = renderHeader({ active: 'trainer' }) + `
    <main class="trainer" id="trainer-body">
      <div class="text-center text-muted">Загрузка…</div>
    </main>
  `;
  bindHeader();

  const body = root.querySelector('#trainer-body');

  const state = {
    tracker: null,
    dna: null,
    active: null,
    drum: [],
    finishedInThisStage: 0,
    busy: false,
    cleanupFns: [],
    showHint: false,
    hintTimer: null,
    stats: null,
    lastSeenMastered: 0,
    combo: 0,
    cleanCombo: 0,
    cleanComboPeak: 0,
    inFlow: false,
    showStageDesc: true,
    typingGate: null,
    // per-collocation counters (reset on new collocation)
    hintOpens: 0,
    hintOpenedDuringAttempt: false,
    consecutiveNoHintSuccesses: 0,
    errorlessCount: 0,
    anyErrorOnCollocation: false,
    skipOffered: false,
  };

  function resetCollocationCounters() {
    state.hintOpens = 0;
    state.hintOpenedDuringAttempt = false;
    state.consecutiveNoHintSuccesses = 0;
    state.errorlessCount = 0;
    state.anyErrorOnCollocation = false;
    state.skipOffered = false;
    state.showHint = false;
    if (state.hintTimer) {
      clearTimeout(state.hintTimer);
      state.hintTimer = null;
    }
  }

  function setShowHint(visible) {
    state.showHint = visible;
    if (state.hintTimer) {
      clearTimeout(state.hintTimer);
      state.hintTimer = null;
    }
    if (visible) {
      state.hintOpens += 1;
      state.hintOpenedDuringAttempt = true;
      state.hintTimer = setTimeout(() => {
        state.showHint = false;
        state.hintTimer = null;
        applyHintVisibility();
      }, 9000);
    }
    applyHintVisibility();
  }

  function applyHintVisibility() {
    const hintBlock = body.querySelector('.hint-block');
    if (!hintBlock) return;
    hintBlock.classList.toggle('showing-english', state.showHint);
    const phraseEn = hintBlock.querySelector('.phrase-en');
    if (phraseEn) phraseEn.setAttribute('aria-hidden', String(!state.showHint));
    const hintControls = hintBlock.querySelector('.hint-controls');
    if (hintControls) {
      hintControls.innerHTML = renderEyeButton(state.showHint);
      const newBtn = hintControls.querySelector('#eye-toggle');
      if (newBtn) newBtn.addEventListener('click', () => setShowHint(!state.showHint));
    }
  }

  async function loadStats() {
    try {
      state.stats = await api.progress.stats();
    } catch {}
  }

  async function loadTypingGate() {
    try {
      state.typingGate = await api.auth.typingProfile();
    } catch {
      state.typingGate = null;
    }
  }

  async function maybeShowCalibrationAnnouncement() {
    if (!state.typingGate?.announceNeeded) return;
    await showCalibrationModal(state.typingGate);
  }

  async function loadNext() {
    try {
      await Promise.all([loadStats(), loadTypingGate()]);
      await maybeShowCalibrationAnnouncement();
      const next = await api.collocations.next();
      if (!next.collocation) {
        body.innerHTML = `<div class="text-center">
          <h2>Всё выучено на этом уровне! 🎉</h2>
          <p class="text-muted">Попробуйте переключиться на другой уровень в настройках.</p>
          <a href="/" class="btn" data-link>На главную</a>
        </div>`;
        return;
      }
      const prevId = state.active?.collocation?.id;
      state.active = next;
      state.active.stageAttempts = next.stageAttempts || [];
      store.currentCollocation = next.collocation;
      store.currentProgress = next.progress;
      store.baseline = next.baseline;
      if (prevId !== next.collocation.id) {
        resetCollocationCounters();
      }
      const drumData = await api.collocations.drum();
      state.drum = drumData.drum;
      render();
    } catch (err) {
      body.innerHTML = `<div class="text-center text-muted">Ошибка: ${err.message}</div>`;
    }
  }

  function cleanupTracker() {
    if (state.tracker) {
      try { state.tracker.destroy(); } catch {}
      state.tracker = null;
    }
  }

  function render() {
    cleanupTracker();
    const { collocation, progress } = state.active;
    const stage = progress.stage;
    const canToggleHint = stage !== 'drum_blind';
    const englishVisible = canToggleHint && state.showHint;

    document.body.classList.toggle('phase-verify', phaseFor(stage) === 'verify');
    document.body.classList.toggle('phase-learn', phaseFor(stage) === 'learn');

    const russianText = collocation.russian;
    const englishText = collocation.english;

    const progressHtml = stage === 'initial_typing' ? renderInitialProgress() : renderDrumStreakBar();
    const drumCount = state.drum?.length ?? 0;
    const drumSummary = (stage === 'drum_hint' || stage === 'drum_blind')
      ? `<div class="drum-count text-muted text-center">Сейчас в работе: ${drumCount} ${pluralize(drumCount, 'фраза', 'фразы', 'фраз')}</div>`
      : '';

    body.innerHTML = `
      ${renderLevelProgress()}
      ${renderGateToggle()}
      ${renderPhaseHeader(stage, progress, state.showStageDesc)}
      <div class="hint-block ${englishVisible ? 'showing-english' : ''}">
        <div class="phrase-swap">
          <span class="phrase phrase-ru">${escapeHtml(russianText)}</span>
          <span class="phrase phrase-en" aria-hidden="${!englishVisible}">${escapeHtml(englishText)}</span>
        </div>
        ${canToggleHint ? `<div class="hint-controls">${renderEyeButton(state.showHint)}</div>` : ''}
      </div>

      ${renderSingleInput()}

      <div class="keystroke-dna-mount" id="dna-mount"></div>

      <div class="live-metrics">
        <span>Темп: <strong id="live-wpm">—</strong> слов/мин</span>
        <span>Ошибок: <strong id="live-errors">0</strong></span>
        ${renderComboChip()}
      </div>
      ${progressHtml}
      ${drumSummary}
    `;

    const eyeBtn = body.querySelector('#eye-toggle');
    if (eyeBtn) {
      eyeBtn.addEventListener('click', () => setShowHint(!state.showHint));
    }

    const descToggle = body.querySelector('#toggle-desc');
    if (descToggle) {
      descToggle.addEventListener('click', () => {
        state.showStageDesc = !state.showStageDesc;
        render();
      });
    }

    const dnaMount = body.querySelector('#dna-mount');
    if (dnaMount) {
      state.dna = new KeystrokeDNA(dnaMount);
    } else {
      state.dna = null;
    }

    if (stage === 'initial_typing') {
      bindSingleInput(onInitialSubmit);
    } else {
      bindSingleInput(onDrumSubmit);
    }

    bindGateToggle();
  }

  function initialEncourager(done, total) {
    const next = Math.min(done + 1, total);
    if (done === 0) return `Поехали — это первый повтор из ${total}`;
    if (done < 3) return `${next}-й из ${total}. Пальцы только разминаются`;
    if (done < 8) return `${next}-й из ${total}. Уже идёт увереннее`;
    if (done < total) return `${next}-й из ${total}. Ещё чуть-чуть — и едем дальше`;
    return `${total} из ${total} — кайф 🔥`;
  }

  function renderComboChip() {
    if (state.cleanCombo >= 2) {
      const fireCls = state.cleanCombo >= 5 ? 'fire' : '';
      const label = state.cleanCombo >= 5
        ? `🔥 В потоке ×${state.cleanCombo}`
        : `⚡ Чисто ×${state.cleanCombo}`;
      return `<span class="combo-chip ${fireCls}">${label}</span>`;
    }
    if (state.combo < 2) return '';
    return `<span class="combo-chip">⚡ ×${state.combo} подряд</span>`;
  }

  function renderLevelProgress() {
    const s = state.stats;
    if (!s) return '';
    const masteredPct = s.total > 0 ? (s.mastered / s.total) * 100 : 0;
    const inProgressPct = s.total > 0 ? ((s.inProgress ?? 0) / s.total) * 100 : 0;
    const insight = levelInsight(s.level, s.mastered, s.total);
    const parked = s.parked ?? 0;
    const parkedChip = parked > 0
      ? `<span class="level-chip parked" title="Эти фразы пока на полке — вернутся, когда станешь сильнее">🅿️ ${parked}</span>`
      : '';
    const inProgressChip = (s.inProgress ?? 0) > 0
      ? `<span class="level-chip in-progress">⚙️ ${s.inProgress} в работе</span>`
      : '';
    return `
      <div class="level-progress">
        <div class="level-progress-row">
          <span class="level-tag">${s.level}</span>
          <span class="level-mastered"><strong>${s.mastered}</strong>/${s.total} <span class="text-muted">выучено</span></span>
          ${inProgressChip}
          ${parkedChip}
        </div>
        <div class="progress-bar layered">
          <div class="fill in-progress" style="width: ${masteredPct + inProgressPct}%"></div>
          <div class="fill mastered" style="width: ${masteredPct}%"></div>
        </div>
        ${insight ? `<div class="level-insight"><span class="insight-icon">✨</span><span>${insight}</span></div>` : ''}
      </div>
    `;
  }

  function levelInsight(level, mastered, total) {
    const pct = total > 0 ? mastered / total : 0;
    const levelMeta = {
      A1: {
        goal: 'survival English — приветствия, знакомства, базовые действия',
        target: 'IELTS 3.5-4.0',
        covered20: 'ситуаций типа «здравствуй/спасибо/как дела»',
        covered50: 'большинства бытовых просьб и коротких обменов',
        covered80: 'практически всего уровня "я в чужой стране и не пропаду"',
      },
      A2: {
        goal: 'повседневная речь — покупки, транспорт, расписание, простые истории',
        target: 'IELTS 4.0-5.0',
        covered20: 'базовых phrasal verbs и бытовых связок',
        covered50: 'уверенных разговоров о ежедневных делах',
        covered80: 'свободы в типичных туристических и бытовых ситуациях',
      },
      B1: {
        goal: 'уверенный разговорный + простой письменный английский',
        target: 'IELTS 5.0-5.5',
        covered20: '~60% самых частотных verb+noun связок в разговоре',
        covered50: 'основного набора для work email и small talk',
        covered80: 'комфортного общения в стандартных сценариях на работе и путешествиях',
      },
      B2: {
        goal: 'академический английский — ядро Writing Task 2 и Speaking Part 3',
        target: 'IELTS 5.5-7.0',
        covered20: 'базовых «скелетов» IELTS Writing: play a role, have an impact, lead to',
        covered50: '~70% chunks, которые ждут экзаменаторы на Band 6.5',
        covered80: 'набора, с которым можно уверенно претендовать на Band 7+',
      },
    };
    const meta = levelMeta[level];
    if (!meta) return '';
    if (mastered === 0) {
      return `Этот уровень — про <strong>${meta.goal}</strong>. Цель: ${meta.target}.`;
    }
    if (pct < 0.25) return `Первые фразы уровня уже покрывают базу ${meta.covered20}.`;
    if (pct < 0.6) return `Уже хватает для ${meta.covered50}.`;
    if (pct < 0.95) return `Почти доходишь до ${meta.covered80}.`;
    return `Уровень ${level} закрыт — ${meta.target} в кармане 🏆`;
  }

  function levelEncourager(done, total) {
    if (total === 0) return 'Загружаем фразы…';
    if (done === 0) return `Стартуем 🚀 впереди <strong>${total}</strong> ${pluralize(total, 'фраза', 'фразы', 'фраз')}`;
    if (done === total) return `Уровень пройден полностью! 🏆`;
    const left = total - done;
    const pct = Math.round((done / total) * 100);
    if (done < 5) return `Первые шаги сделаны 👟 уже <strong>${done}</strong> в коллекции`;
    if (pct < 25) return `Уже <strong>${done}</strong> твои. Осталось ${left} — ты в теме 💪`;
    if (pct < 50) return `Четверть пути позади 🔥 <strong>${done}</strong> из ${total}`;
    if (pct < 75) return `Больше половины — огонь! <strong>${done}</strong> из ${total}`;
    if (pct < 90) return `Финишная прямая 🎯 <strong>${done}</strong> из ${total}`;
    return `Почти всё! Осталось ${left} ${pluralize(left, 'фраза', 'фразы', 'фраз')}`;
  }

  function renderEyeButton(visible) {
    const openEye = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`;
    const closedEye = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`;
    const label = visible ? 'Скрыть подсказку' : 'Показать перевод';
    const teaser = visible
      ? `<span class="eye-teaser eye-teaser--visible">9 сек на английском</span>`
      : `<span class="eye-teaser">тык — превратит в английский на 9 сек 👀</span>`;
    return `
      <div class="eye-wrap">
        ${teaser}
        <button type="button" class="eye-btn" id="eye-toggle" aria-label="${label}" title="${label}">${visible ? openEye : closedEye}</button>
      </div>
    `;
  }

  function renderInitialProgress() {
    const total = 15;
    const history = state.active.stageAttempts || [];
    const segs = [];
    for (let i = 0; i < total; i++) {
      const a = history[i];
      const cls = a ? (a.textMatches ? 'ok' : 'fail') : '';
      segs.push(`<div class="seg ${cls}"></div>`);
    }
    return `
      <div class="typing-area">
        <div class="segment-bar" style="grid-template-columns: repeat(${total}, 1fr);">${segs.join('')}</div>
        <div class="text-muted text-center mt-2">${initialEncourager(history.length, total)}</div>
      </div>
    `;
  }

  function renderSingleInput() {
    return `
      <div class="drum-single">
        <input
          type="text"
          id="drum-input"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          inputmode="text"
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
          autofocus
        />
      </div>
    `;
  }

  function renderDrumStreakBar() {
    const { progress } = state.active;
    let need = 0;
    let done = 0;
    if (progress.stage === 'drum_hint') {
      need = 3;
      done = progress.drumHintSuccessCount;
    } else if (progress.stage === 'drum_blind') {
      need = 2;
      done = progress.drumBlindSuccessCount;
    } else {
      return '';
    }
    const totalAttempts = (state.active.stageAttempts || []).length;
    const segs = Array.from({ length: need }, (_, i) =>
      `<div class="seg ${i < done ? 'ok' : ''}"></div>`
    ).join('');
    return `
      <div class="drum-streak">
        <div class="segment-bar streak-bar" style="grid-template-columns: repeat(${need}, 1fr);">${segs}</div>
        <div class="streak-meta text-muted">
          <strong>${done}/${need}</strong> подряд · ${totalAttempts} ${pluralize(totalAttempts, 'попытка', 'попытки', 'попыток')} в этой стадии
        </div>
      </div>
    `;
  }

  function bindSingleInput(onSubmit) {
    const input = body.querySelector('#drum-input');
    if (!input) return;
    input.focus();
    attachTracker(input, { onSubmit });
  }

  function attachTracker(input, { onSubmit }) {
    const { collocation } = state.active;
    state.tracker = new KeystrokeTracker(input, collocation.english);

    const wpmEl = body.querySelector('#live-wpm');
    const errEl = body.querySelector('#live-errors');

    const updateLive = () => {
      if (!state.tracker) return;
      const m = state.tracker.computeMetrics();
      wpmEl.textContent = m.wpm || '—';
      errEl.textContent = m.error_count;
      if (state.dna && Array.isArray(m.keystroke_intervals)) {
        state.dna.update(m.keystroke_intervals);
      }
    };

    input.addEventListener('input', updateLive);

    let layoutToastShown = false;
    const onWrongLayout = () => {
      if (layoutToastShown) return;
      layoutToastShown = true;
      toast('Переключи раскладку на английскую', { type: 'error', duration: 2400 });
      setTimeout(() => { layoutToastShown = false; }, 2400);
    };
    input.addEventListener('wrong-layout', onWrongLayout);
    state.cleanupFns.push(() => input.removeEventListener('wrong-layout', onWrongLayout));

    const onKey = async (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (state.busy) return;
        if (input.value.length === 0) return;
        state.busy = true;
        input.setAttribute('readonly', 'readonly');
        try {
          await onSubmit(input);
        } finally {
          state.busy = false;
        }
      }
    };
    input.addEventListener('keydown', onKey);
    state.cleanupFns.push(() => {
      input.removeEventListener('keydown', onKey);
      input.removeEventListener('input', updateLive);
    });
  }

  async function onInitialSubmit(input) {
    const metrics = state.tracker.computeMetrics();
    const hintWasOpened = state.hintOpenedDuringAttempt;
    const outcome = await submit(metrics, input.value);
    const prevProgress = state.active.progress;
    state.active.progress = {
      ...prevProgress,
      stage: outcome.progress.stage,
      status: outcome.progress.status,
      initialRepsDone: outcome.progress.initialRepsDone,
      drumHintSuccessCount: outcome.progress.drumHintSuccessCount,
      drumBlindSuccessCount: outcome.progress.drumBlindSuccessCount,
    };
    state.active.stageAttempts = [
      ...(state.active.stageAttempts || []),
      { isSuccessful: outcome.isSuccessful, textMatches: outcome.textMatches },
    ];

    const hadError = metrics.error_count > 0 || !outcome.textMatches;
    if (hadError) state.anyErrorOnCollocation = true;
    const errorless = !hadError;
    if (errorless) {
      state.errorlessCount += 1;
      state.combo += 1;
      flashInput(input, 'success');
    } else {
      state.combo = 0;
      flashInput(input, 'error');
    }
    if (errorless && !hintWasOpened) {
      state.consecutiveNoHintSuccesses += 1;
    } else {
      state.consecutiveNoHintSuccesses = 0;
    }
    // Reset per-attempt hint-opened flag (hint stays visible if still within 9s)
    state.hintOpenedDuringAttempt = state.showHint;

    if (outcome.progress.transitioned) {
      const parkedPhrase = state.active?.collocation?.english;
      await showStageTransition('drum_hint', { phrase: parkedPhrase });
      await loadNext();
      return;
    }

    if (shouldOfferSkip()) {
      state.skipOffered = true;
      showSkipOffer();
      return;
    }

    render();
  }

  function shouldOfferSkip() {
    if (state.skipOffered) return false;
    if (state.anyErrorOnCollocation) return false;
    if (state.active?.progress?.stage !== 'initial_typing') return false;
    if (state.consecutiveNoHintSuccesses >= 3) return true;
    if (state.errorlessCount >= 8 && state.hintOpens <= 1) return true;
    return false;
  }

  function showSkipOffer() {
    cleanupTracker();
    const modal = document.createElement('div');
    modal.className = 'modal-backdrop';
    modal.innerHTML = `
      <div class="modal">
        <h2>У тебя хорошо получается 👍</h2>
        <p class="text-muted">Можешь долбить эту фразу дальше для надёжности — или сразу пойти к следующему шагу, где фразы будут вперемешку.</p>
        <div style="display:flex; gap:10px; margin-top: 18px; flex-wrap: wrap;">
          <button id="skip-continue" class="btn-ghost">Ещё поупражняюсь</button>
          <button id="skip-jump">Дальше, к следующему шагу</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    modal.querySelector('#skip-continue').addEventListener('click', () => {
      modal.remove();
      render();
    });
    modal.querySelector('#skip-jump').addEventListener('click', async () => {
      modal.remove();
      try {
        await api.progress.skipToDrum(state.active.progress.id);
        toast('Перехожу в закрепление →', { type: 'success' });
        await loadNext();
      } catch (err) {
        toast('Не получилось: ' + (err.message || ''), { type: 'error' });
        render();
      }
    });
  }

  async function onDrumSubmit(input) {
    const metrics = state.tracker.computeMetrics();
    const outcome = await submit(metrics, input.value);
    const stageBefore = state.active.progress.stage;

    if (outcome.isSuccessful) {
      state.combo += 1;
      flashInput(input, 'success');
    } else {
      state.combo = 0;
      flashInput(input, 'error');
    }

    let nextModal = null;
    if (outcome.nextAction === 'mastered') {
      toast('🌟 Фраза твоя!', { type: 'success', duration: 2500 });
      burstConfetti(14);
    } else if (outcome.nextAction === 'transition_to_blind') {
      nextModal = 'drum_blind';
    } else if (outcome.nextAction === 'ejected_to_restart') {
      nextModal = 'ejected';
    } else if (outcome.nextAction === 'parked') {
      nextModal = 'parked';
    } else if (!outcome.isSuccessful) {
      toast(outcome.textMatches ? 'Чуть не хватило — давай ещё' : 'Не совпало — попробуй снова, ты близко', { type: outcome.textMatches ? 'info' : 'error' });
    } else {
      toast('Чётко ✓', { type: 'success', duration: 1200 });
    }

    const prevMastered = state.stats?.mastered ?? 0;
    if (nextModal) {
      const phrase = state.active?.collocation?.english;
      await showStageTransition(nextModal, { phrase });
    }
    await loadNext();
    const newMastered = state.stats?.mastered ?? prevMastered;
    if (newMastered > prevMastered && newMastered % 9 === 0) {
      showBatchMilestone(newMastered);
    }
  }

  function flashInput(input, kind) {
    if (!input) return;
    const cls = kind === 'success' ? 'just-success' : 'just-error';
    input.classList.remove('just-success', 'just-error');
    void input.offsetWidth;
    input.classList.add(cls);
  }

  function burstConfetti(count = 30) {
    const colors = ['#b3ff3a', '#3af2ff', '#ff3aa4', '#7fff9e', '#ffcf47', '#ff5170'];
    const layer = document.createElement('div');
    layer.className = 'confetti-layer';
    document.body.appendChild(layer);
    for (let i = 0; i < count; i++) {
      const bit = document.createElement('div');
      bit.className = 'confetti-bit';
      const x = 30 + Math.random() * 40;
      const dx = (Math.random() - 0.5) * 600;
      const dur = 1600 + Math.random() * 1400;
      const rot = (Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 720);
      bit.style.left = x + '%';
      bit.style.background = colors[i % colors.length];
      bit.style.setProperty('--dx', dx + 'px');
      bit.style.setProperty('--rot', rot + 'deg');
      bit.style.setProperty('--dur', dur + 'ms');
      bit.style.animationDelay = Math.random() * 180 + 'ms';
      layer.appendChild(bit);
    }
    setTimeout(() => layer.remove(), 3800);
  }

  function showStageTransition(key, ctx = {}) {
    return new Promise((resolve) => {
      const meta = STAGE_TRANSITIONS[key];
      if (!meta) return resolve();
      cleanupTracker();
      const fill = (s) =>
        String(s).replace(/\{phrase\}/g, ctx.phrase ? escapeHtml(ctx.phrase) : '');
      const modal = document.createElement('div');
      modal.className = 'modal-backdrop';
      modal.innerHTML = `
        <div class="modal stage-transition-modal">
          <div class="transition-icon" style="background:${meta.bg};">${meta.icon}</div>
          <div class="transition-kicker">${meta.kicker}</div>
          <h2>${fill(meta.title)}</h2>
          <p class="text-muted">${fill(meta.body)}</p>
          <ul class="transition-rules">${meta.rules.map((r) => `<li>${fill(r)}</li>`).join('')}</ul>
          <button id="stage-transition-go">${meta.cta}</button>
        </div>
      `;
      document.body.appendChild(modal);
      const close = () => { modal.remove(); resolve(); };
      modal.querySelector('#stage-transition-go').addEventListener('click', close);
    });
  }

  function milestoneContext(level, mastered, total) {
    const pct = total > 0 ? mastered / total : 0;
    const pool = {
      A1: [
        'Этого уже хватит, чтобы здороваться, представляться и благодарить — как местный.',
        'Ты можешь заказать кофе, спросить дорогу и не теряться в базовых ситуациях.',
        'Survival English — уже в кармане.',
      ],
      A2: [
        'Покупки, транспорт, расписание — на этих фразах ты уже плывёшь.',
        'Тёплый small talk на простые темы — без ступора.',
        'Это верхушка A2 — дальше пойдёт уверенный разговорный.',
      ],
      B1: [
        'Эти фразы встречаются в ~60% обычных разговоров. Серьёзный фундамент.',
        'Work email и неформальное общение — уже в зоне комфорта.',
        'Осталось чуть-чуть — и перейдёшь на академический B2.',
      ],
      B2: [
        'Это ядро Writing Task 2. Экзаменаторы отмечают такие связки как «Band 6+».',
        'С таким набором Speaking Part 3 — уже без паники.',
        'Ты почти у Band 7. Дальше — дожим до полного владения.',
      ],
    };
    const pick = pool[level] ?? pool.B1;
    if (pct < 0.35) return pick[0];
    if (pct < 0.75) return pick[1];
    return pick[2];
  }

  function showBatchMilestone(mastered) {
    const batchIdx = Math.floor(mastered / 9);
    burstConfetti(60);
    const modal = document.createElement('div');
    modal.className = 'modal-backdrop milestone';
    const level = state.stats?.level ?? 'B1';
    const total = state.stats?.total ?? 50;
    const pct = total > 0 ? Math.round((mastered / total) * 100) : 0;
    const contextMsg = milestoneContext(level, mastered, total);
    modal.innerHTML = `
      <div class="modal milestone-modal">
        <div class="milestone-badge">${mastered}</div>
        <h2>${mastered} ${pluralize(mastered, 'фраза', 'фразы', 'фраз')} в памяти 🎉</h2>
        <p class="text-muted">${pct}% уровня ${level} — твои. ${contextMsg}</p>
        <button id="milestone-continue">Да, поехали</button>
      </div>
    `;
    document.body.appendChild(modal);
    modal.querySelector('#milestone-continue').addEventListener('click', () => modal.remove());
  }

  async function submit(metrics, typedText) {
    const { collocation, progress } = state.active;
    const payload = {
      progressId: progress.id,
      collocationId: collocation.id,
      stage: progress.stage,
      typedText,
      startedAt: metrics.startedAt.toISOString(),
      completedAt: metrics.completedAt.toISOString(),
      durationMs: metrics.duration_ms,
      wpm: metrics.wpm,
      charCount: metrics.char_count,
      errorCount: metrics.error_count,
      correctedCount: metrics.corrected_count,
      stddevIki: metrics.stddev_iki,
      pauseCount: metrics.pause_count,
      keystrokeIntervals: metrics.keystroke_intervals,
    };
    const response = await api.attempts.submit(payload);
    applyGamificationOutcome(response, metrics);
    return response;
  }

  function applyGamificationOutcome(outcome, metrics) {
    if (outcome.form) {
      store.form = { score: outcome.form.score, tier: outcome.form.tier };
      updateHeaderForm(outcome.form);
    }
    if (outcome.dailyGoal) {
      store.dailyGoal = outcome.dailyGoal;
    }
    if (outcome.isCleanRun) {
      state.cleanCombo += 1;
      if (state.cleanCombo > state.cleanComboPeak) state.cleanComboPeak = state.cleanCombo;
      if (state.cleanCombo >= 5 && !state.inFlow) enterFlow();
    } else {
      if (state.inFlow) exitFlow();
      state.cleanCombo = 0;
    }
    if (outcome.rhythmSyncScore && outcome.rhythmSyncScore >= 0.8 && state.dna) {
      state.dna.flashSync();
    }
    if (outcome.dailyGoal?.completedNow) {
      toast('Цель дня выполнена 🎯', { type: 'success', duration: 2500 });
    }
    if (outcome.form?.delta > 0) {
      // fire off a tiny badge update hint
      // (FormIndicator in header will be refreshed on next nav)
    }
  }

  function updateHeaderForm(form) {
    const nav = document.querySelector('.header .nav');
    if (!nav) return;
    let badge = nav.querySelector('.header-form');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'header-form';
      const email = nav.querySelector('.text-muted');
      if (email) nav.insertBefore(badge, email);
      else nav.appendChild(badge);
    }
    badge.title = `Форма · тир ${form.tier}`;
    badge.textContent = `⚡ ${Math.round(form.score)}`;
    badge.classList.remove('pulse');
    void badge.offsetWidth;
    badge.classList.add('pulse');
  }

  function enterFlow() {
    state.inFlow = true;
    document.body.classList.add('flow-mode');
    toast('⚡ Поймал поток — держи ритм!', { type: 'success', duration: 1800 });
  }

  function exitFlow() {
    state.inFlow = false;
    document.body.classList.remove('flow-mode');
  }

  function showCalibrationModal(gate) {
    return new Promise((resolve) => {
      const wpm = gate.medianWpm != null ? Math.round(gate.medianWpm) : '—';
      const tolPct = Math.round((gate.toleranceWpm ?? 0.20) * 100);
      const wpmFloor = gate.medianWpm != null ? Math.round(gate.medianWpm * (1 - gate.toleranceWpm)) : '—';
      const samples = gate.sampleCount;
      const days = gate.daysSinceFirstAttempt ?? gate.calibrationDays;
      const modal = document.createElement('div');
      modal.className = 'modal-backdrop';
      modal.innerHTML = `
        <div class="modal calibration-modal">
          <div class="calibration-icon">🎯</div>
          <div class="calibration-kicker">Калибровка завершена</div>
          <h2>Переходим на новый уровень</h2>
          <p class="text-muted">
            Эти первые ${days} ${pluralize(days, 'день', 'дня', 'дней')} мы наблюдали, как ты печатаешь, и собрали твой собственный профиль —
            ${samples} ${pluralize(samples, 'попытка', 'попытки', 'попыток')} анализа. Теперь у нас есть твой типичный темп
            и ритм.
          </p>
          <div class="calibration-stats">
            <div class="stat">
              <div class="stat-value">${wpm}</div>
              <div class="stat-label">слов/мин · твоя медиана</div>
            </div>
            <div class="stat">
              <div class="stat-value">±${tolPct}%</div>
              <div class="stat-label">коридор уверенности</div>
            </div>
          </div>
          <p>
            С этого момента появляется новый параметр — <strong>уверенность ввода</strong>. Попытка засчитается, если ты
            держишь темп ≥ <strong>${wpmFloor} слов/мин</strong> и ритм не «рассыпается» сильнее, чем обычно у тебя.
            Это даст более честную оценку — не просто «текст совпал», а «ты действительно владеешь фразой».
          </p>
          <label class="toggle-row">
            <input type="checkbox" id="calib-enable" ${gate.optedOut ? '' : 'checked'} />
            <span class="toggle-track"><span class="toggle-knob"></span></span>
            <span class="toggle-text">
              <strong>Включить контроль уверенности</strong>
              <span class="text-muted">Если заебёшься — отключи в шапке тренажёра, вернёшься в демо-режим</span>
            </span>
          </label>
          <div class="calibration-actions">
            <button id="calib-go" class="btn-primary">Поехали</button>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
      const cb = modal.querySelector('#calib-enable');
      modal.querySelector('#calib-go').addEventListener('click', async () => {
        const wantOptedOut = !cb.checked;
        try {
          if (wantOptedOut !== gate.optedOut) {
            await api.auth.typingProfileSet(wantOptedOut);
          }
          await api.auth.typingProfileAnnounce();
          state.typingGate = await api.auth.typingProfile();
        } catch (err) {
          toast('Не получилось сохранить — попробуй позже', { type: 'error' });
        }
        modal.remove();
        resolve();
      });
    });
  }

  function renderGateToggle() {
    const gate = state.typingGate;
    if (!gate || !gate.calibrated) return '';
    const on = !gate.optedOut;
    return `
      <div class="gate-toggle ${on ? 'on' : 'off'}">
        <span class="gate-toggle-label">${on ? '🎯 Контроль уверенности' : '🛟 Демо-режим'}</span>
        <button type="button" class="gate-toggle-btn" id="gate-toggle-btn" aria-pressed="${on}" title="${on ? 'Выключить контроль' : 'Включить контроль'}">
          <span class="toggle-track sm"><span class="toggle-knob sm"></span></span>
        </button>
      </div>
    `;
  }

  function bindGateToggle() {
    const btn = body.querySelector('#gate-toggle-btn');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const next = !state.typingGate.optedOut;
      try {
        state.typingGate = await api.auth.typingProfileSet(next);
        toast(next ? 'Демо-режим: контроль скорости/ритма выключен' : 'Контроль уверенности включён', {
          type: 'success',
          duration: 2200,
        });
        render();
      } catch (err) {
        toast('Не получилось переключить', { type: 'error' });
      }
    });
  }

  root.__trainerCleanup = () => {
    cleanupTracker();
    for (const fn of state.cleanupFns) try { fn(); } catch {}
  };

  await loadNext();

  return {
    cleanup: () => {
      cleanupTracker();
      for (const fn of state.cleanupFns) try { fn(); } catch {}
      document.body.classList.remove('phase-verify', 'phase-learn');
    },
  };
}

function pluralize(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
